/**
 * Progress bar component.
 *
 * Accessibility:
 * - aria-valuenow, aria-valuemin, aria-valuemax for progress state
 * - Optional aria-label prop for describing what is being measured
 */
import * as React from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { cn } from "@/lib/utils";

/** Semantic intent drives the indicator color (utilization meters). */
type ProgressSemantic = "default" | "positive" | "warning" | "negative";

const semanticIndicator: Record<ProgressSemantic, string> = {
  default: "bg-primary",
  positive: "bg-positive",
  warning: "bg-warning",
  negative: "bg-negative",
};

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> & {
    indicatorClassName?: string;
    /** Semantic color intent for the indicator (default | positive | warning | negative) */
    semantic?: ProgressSemantic;
    /** Accessible label describing what the progress bar measures */
    "aria-label"?: string;
  }
>(
  (
    {
      className,
      value,
      indicatorClassName,
      semantic = "default",
      "aria-label": ariaLabel,
      ...props
    },
    ref,
  ) => (
    <ProgressPrimitive.Root
      ref={ref}
      aria-valuenow={value ?? 0}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={ariaLabel}
      className={cn(
        "relative h-1.5 w-full overflow-hidden rounded-full bg-surface-1",
        className,
      )}
      {...props}
    >
      <ProgressPrimitive.Indicator
        className={cn(
          "h-full w-full flex-1 transition-transform duration-(--dur-base) ease-(--ease-out)",
          semanticIndicator[semantic],
          indicatorClassName,
        )}
        style={{ transform: `translateX(-${100 - (value || 0)}%)` }}
      />
    </ProgressPrimitive.Root>
  ),
);
Progress.displayName = ProgressPrimitive.Root.displayName;

export { Progress };
